import { getPersonaCategoryMeta, IPersonaCategoryMeta } from '@cnt-workspace/data-access';
import {
  DESCRIPTOR_GROUP,
  getCategoryForType,
  IListing,
  ILandType,
  IPrimaryPropertyType,
  IPropertyDescriptor,
  PrimaryPropertyType,
  PropertyDescriptor,
} from '@curbnturf/entities';

/**
 * Display helpers for the three-level listing taxonomy, keyed by the server's
 * camelCase slugs (@curbnturf/objects category-types.ts). cnt-landing's own
 * PRIMARY_PROPERTY_TYPE_META is keyed by the host wizard's kebab-case values,
 * so admin builds on the objects enums instead.
 */

export type PrimaryTypeGroup = 'hospitality' | 'land' | 'other';

export const PRIMARY_TYPE_GROUP_LABELS: Record<PrimaryTypeGroup, string> = {
  hospitality: 'Hospitality',
  land: 'Land-based business',
  other: 'Other venues',
};

/** Same grouping the host wizard's primary-type step uses. */
export const PRIMARY_TYPE_GROUP: Record<IPrimaryPropertyType['type'], PrimaryTypeGroup> = {
  winery: 'hospitality',
  brewery: 'hospitality',
  distillery: 'hospitality',
  rvPark: 'hospitality',
  farm: 'land',
  specialtyFarm: 'land',
  orchard: 'land',
  ranch: 'land',
  woodlands: 'land',
  golfCourse: 'other',
  church: 'other',
  privateHome: 'other',
  custom: 'other',
};

export interface IOption<T extends string> {
  value: T;
  label: string;
}

export interface IOptionGroup<G extends string, T extends string> {
  group: G;
  label: string;
  options: IOption<T>[];
}

export const PRIMARY_TYPE_OPTIONS_BY_GROUP: IOptionGroup<PrimaryTypeGroup, IPrimaryPropertyType['type']>[] = (
  Object.keys(PRIMARY_TYPE_GROUP_LABELS) as PrimaryTypeGroup[]
).map((group) => ({
  group,
  label: PRIMARY_TYPE_GROUP_LABELS[group],
  options: (Object.keys(PRIMARY_TYPE_GROUP) as IPrimaryPropertyType['type'][])
    .filter((t) => PRIMARY_TYPE_GROUP[t] === group)
    .map((t) => ({ value: t, label: PrimaryPropertyType[t] })),
}));

export const DESCRIPTOR_OPTIONS_BY_GROUP: IOptionGroup<'setting' | 'landscape', IPropertyDescriptor['type']>[] = (
  ['setting', 'landscape'] as const
).map((group) => ({
  group,
  label: group === 'setting' ? 'Setting' : 'Landscape',
  options: (Object.keys(DESCRIPTOR_GROUP) as IPropertyDescriptor['type'][])
    .filter((d) => DESCRIPTOR_GROUP[d] === group)
    .map((d) => ({ value: d, label: PropertyDescriptor[d] })),
}));

export function primaryTypeLabel(listing: Pick<IListing, 'primaryType' | 'customPrimaryLabel'>): string {
  if (!listing.primaryType) return '';
  if (listing.primaryType === 'custom') {
    return listing.customPrimaryLabel?.trim() || PrimaryPropertyType.custom;
  }
  return PrimaryPropertyType[listing.primaryType] ?? listing.primaryType;
}

export function descriptorLabel(descriptor: IPropertyDescriptor['type']): string {
  return PropertyDescriptor[descriptor] ?? descriptor;
}

/**
 * The listing's guest-facing category. Prefers the server-derived `category`;
 * falls back to deriving it from `primaryType` (what the server will store on
 * the next property-types save) so an unsaved edit still previews correctly.
 */
export function listingCategoryMeta(
  listing: Pick<IListing, 'category' | 'primaryType'>,
): IPersonaCategoryMeta | undefined {
  const slug = listing.category ?? (listing.primaryType ? getCategoryForType(listing.primaryType) : undefined);
  return slug ? getPersonaCategoryMeta(slug) : undefined;
}

const LAND_TYPE_TO_PRIMARY: Partial<Record<ILandType['type'], IPrimaryPropertyType['type']>> = {
  winery: 'winery',
  brewery: 'brewery',
  distillery: 'distillery',
  orchard: 'orchard',
  farm: 'farm',
  backyard: 'privateHome',
  driveway: 'privateHome',
  curbside: 'privateHome',
  forest: 'woodlands',
};

/** Priority order when a legacy listing has several land types. */
const LAND_TYPE_PRIORITY = Object.keys(LAND_TYPE_TO_PRIMARY) as ILandType['type'][];

/**
 * Best-effort migration aid for listings created before primaryType existed:
 * venue land types (winery/brewery/distillery/orchard/farm) win, then
 * residential settings map to privateHome, then forest to woodlands. Every
 * non-venue land type is spelled identically to an IPropertyDescriptor, so
 * those carry over as descriptors.
 */
export function suggestFromLandTypes(landTypes: ILandType['type'][] | undefined): {
  primaryType?: IPrimaryPropertyType['type'];
  descriptors: IPropertyDescriptor['type'][];
} {
  const types = landTypes ?? [];
  const match = LAND_TYPE_PRIORITY.find((t) => types.includes(t));
  const descriptors = types.filter((t): t is IPropertyDescriptor['type'] => t in DESCRIPTOR_GROUP);
  return { primaryType: match ? LAND_TYPE_TO_PRIMARY[match] : undefined, descriptors };
}
